import { useLayoutEffect, useRef } from "react";
import {
  Apple,
  ArrowLeft,
  BarChart3,
  Bug,
  Check,
  CheckCircle2,
  ChevronDown,
  Code2,
  FileText,
  Globe,
  Home,
  Layers,
  ListChecks,
  Loader2,
  Play,
  Send,
  Settings,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Video,
  X,
  Zap,
} from "lucide-react";
import FrothAiIcon from "../common/FrothAiIcon";
import "../../css/FrothAiScenes.css";

// Animated product walkthroughs for "See FrothAI in action" (FrothAiGuides.js).
// Each scene is a pure function of `t`, the milliseconds into the guide, so the
// guide owns one clock: it lights the numbered steps and plays the scene in step.
// The scenes copy the real screens of the product (frothtestops-frontend-revamp):
// GenerateAITestCase, CreateTestCaseForm, CreateAutomationScript and the
// Ask FrothAI drawer. At t = ms they show the finished state, which is also what
// reduced-motion visitors see.

/* ── timeline helpers ─────────────────────────────────────────────── */
const clamp01 = v => Math.max(0, Math.min(1, v));
const ease = p => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
const prog = (t, from, to) => clamp01((t - from) / (to - from));
const typed = (text, t, start, msPerChar = 32) =>
  text.slice(0, Math.max(0, Math.floor((t - start) / msPerChar)));
const flash = (t, at, len = 900) => t >= at && t < at + len;
const cx = (...names) => names.filter(Boolean).join(" ");

/* ── cursor ───────────────────────────────────────────────────────── */
// path: [[arriveAtMs, target]] where target is a data-cur name inside the stage
// or an [x%, y%] pair. The cursor glides to each target over MOVE_MS before it.
const MOVE_MS = 620;

function Cursor({ stageRef, t, path, clicks = [] }) {
  const ref = useRef(null);

  // Targets are measured every frame, so the cursor lands on them whatever the stage size.
  useLayoutEffect(() => {
    const stage = stageRef.current;
    const el = ref.current;
    if (!stage || !el) return;
    const box = stage.getBoundingClientRect();
    if (!box.width) return;
    const at = target => {
      if (Array.isArray(target)) return target;
      const node = stage.querySelector(`[data-cur="${target}"]`);
      if (!node) return null;
      const r = node.getBoundingClientRect();
      return [
        ((r.left + r.width / 2 - box.left) / box.width) * 100,
        ((r.top + r.height / 2 - box.top) / box.height) * 100,
      ];
    };
    let i = 0;
    while (i < path.length - 1 && path[i + 1][0] <= t) i++;
    let p = at(path[i][1]) || [50, 50];
    const next = path[i + 1];
    if (next && t >= path[i][0] && t > next[0] - MOVE_MS) {
      const q = at(next[1]);
      if (q) {
        const k = ease(prog(t, next[0] - MOVE_MS, next[0]));
        p = [p[0] + (q[0] - p[0]) * k, p[1] + (q[1] - p[1]) * k];
      }
    }
    el.style.left = `${p[0]}%`;
    el.style.top = `${p[1]}%`;
  });

  const down = clicks.some(c => t >= c && t < c + 300);
  return (
    <span ref={ref} className={cx("faw-cursor", down && "is-down")}>
      <svg viewBox="0 0 24 24" width="100%" height="100%">
        <path
          d="M4 2.5 19.5 12l-6.9 1.6-3.4 6.4L4 2.5Z"
          fill="#0d1424"
          stroke="#fff"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

/* ── app chrome ───────────────────────────────────────────────────── */
const RAIL = [Home, BarChart3, Layers, ListChecks, Sparkles, Bug, ShieldCheck, Code2, FileText, Settings];

function Screen({ stageRef, crumbs, nav, dim, children, overlay }) {
  return (
    <div className="faw-stage" ref={stageRef}>
      <div className="faw-screen">
        <div className="faw-app">
          <aside className="faw-rail">
            <span className="faw-rail__logo">F</span>
            {RAIL.map((Icon, i) => (
              <span key={i} className={cx("faw-rail__item", i === nav && "is-on")}>
                <Icon />
              </span>
            ))}
          </aside>
          <div className="faw-body">
            <div className="faw-top">
              <span className="faw-top__project">
                FrothShop Web Store <ChevronDown />
              </span>
              <span className="faw-top__avatar">QA</span>
            </div>
            <div className={cx("faw-content", dim && "is-dim")}>
              <div className="faw-crumbs">
                {crumbs.map((c, i) => (
                  <span key={c} className={i === crumbs.length - 1 ? "is-last" : undefined}>
                    {c}
                  </span>
                ))}
              </div>
              {children}
            </div>
          </div>
        </div>
        {overlay}
      </div>
    </div>
  );
}

function Toast({ show, children }) {
  return (
    <div className={cx("faw-toast", show && "is-in")}>
      <CheckCircle2 />
      {children}
    </div>
  );
}

/* ── 1. Generate from Requirements ────────────────────────────────── */
const CRITERIA = [
  { label: "Type", items: ["Functional", "Non-Functional"] },
  { label: "Execution Bucket", items: ["Smoke", "Sanity", "Regression", "UAT"] },
  { label: "Coverage Type", items: ["Positive", "Negative", "Edge / Boundary", "Exception", "UI / UX", "Data-Driven"] },
];
const PICKS = { Functional: 900, Regression: 1550, Positive: 2200, Negative: 2800, "Edge / Boundary": 3400 };
const NOTE = "Cover guest and signed-in checkout, saved addresses and tax totals";
const NOTE_AT = 4500;
const GEN_AT = 6900;
const GEN_END = 8550;
const REVIEW_AT = 8700;
const INSERT_AT = 11500;

const GENERATED = [
  {
    id: "FSHOP-TS-3",
    tag: "Positive",
    title: "Add, update and remove cart items",
    cases: ["Adding an item updates the cart badge and subtotal", "Changing quantity recalculates taxes and total"],
  },
  {
    id: "FSHOP-TS-4",
    tag: "Positive",
    title: "Guest and registered checkout",
    cases: ["Signed-in user can pick a saved address"],
  },
  {
    id: "FSHOP-TS-5",
    tag: "Negative",
    title: "Checkout with missing or stale details",
    cases: ["An expired card shows a recoverable error", "Out-of-range ZIP code blocks tax calculation"],
  },
];
const GEN_CASES = GENERATED.reduce((n, s) => n + s.cases.length, 0);

function RequirementsScene({ t }) {
  const stageRef = useRef(null);
  const review = t >= REVIEW_AT;
  const busy = t >= GEN_AT && t < GEN_END;
  const pct = Math.round(prog(t, GEN_AT + 100, GEN_END) * 100);
  let row = 0;

  return (
    <Screen
      stageRef={stageRef}
      nav={3}
      crumbs={["Requirements", "FSHOP-REQ-12", "Generate with FrothAI"]}
      overlay={
        <>
          <div className={cx("faw-bgbar", busy && "is-in")}>
            <Loader2 className="faw-spin" />
            <span>
              FrothAI is generating in the background <em>· you can keep working</em>
            </span>
            <b>{pct}%</b>
            <i style={{ width: `${pct}%` }} />
          </div>
          <Toast show={t >= INSERT_AT + 150}>
            Inserted {GENERATED.length} scenarios and {GEN_CASES} test cases
          </Toast>
          <Cursor
            stageRef={stageRef}
            t={t}
            path={[
              [0, [80, 96]],
              [PICKS.Functional, "c-Functional"],
              [PICKS.Regression, "c-Regression"],
              [PICKS.Positive, "c-Positive"],
              [PICKS.Negative, "c-Negative"],
              [PICKS["Edge / Boundary"], "c-Edge / Boundary"],
              [4300, "note"],
              [GEN_AT, "gen"],
              [8200, [62, 74]],
              [INSERT_AT, "insert"],
              [12500, [88, 90]],
            ]}
            clicks={[...Object.values(PICKS), 4300, GEN_AT, INSERT_AT]}
          />
        </>
      }
    >
      {!review ? (
        <div className="faw-view" key="form">
          <div className="faw-head">
            <h4 className="faw-title faw-title--brand">AI Test Scenario &amp; Test Case</h4>
            <span className="faw-req">
              <span className="faw-mono">FSHOP-REQ-12</span> Shopping cart and checkout
            </span>
          </div>
          <div className="faw-cols faw-cols--req">
            <div className="faw-stack">
              <div>
                <span className="faw-label">Additional Information</span>
                <div className={cx("faw-input faw-input--area", t >= 4300 && t < 6600 && "is-focus")} data-cur="note">
                  {typed(NOTE, t, NOTE_AT, 28)}
                  {t >= 4300 && t < 6600 && <span className="faw-caret" />}
                </div>
              </div>
              <div className="faw-gencard">
                <div className="faw-gencard__head">
                  <span className="faw-gencard__bolt">
                    <Zap />
                  </span>
                  Generate AI-Powered Test Assets
                </div>
                <ul className="faw-ticks">
                  <li>
                    <CheckCircle2 /> Generate Test Scenarios
                  </li>
                  <li>
                    <CheckCircle2 /> Category-Based Test Cases
                  </li>
                  <li>
                    <CheckCircle2 /> Interactive Review
                  </li>
                </ul>
                <span
                  className={cx("faw-btn faw-btn--ai", busy && "is-busy", flash(t, GEN_AT, 300) && "is-press")}
                  data-cur="gen"
                >
                  {busy ? <Loader2 className="faw-spin" /> : <FrothAiIcon size={12} />}
                  {busy ? "Generating…" : "Generate with FrothAI"}
                </span>
              </div>
            </div>
            <div className="faw-card">
              <div className="faw-card__title">Generation Criteria</div>
              {CRITERIA.map(group => (
                <div key={group.label} className="faw-crit">
                  <span className="faw-label">{group.label}</span>
                  <div className="faw-chips">
                    {group.items.map(item => (
                      <span
                        key={item}
                        data-cur={`c-${item}`}
                        className={cx(
                          "faw-chip",
                          PICKS[item] !== undefined && t >= PICKS[item] && "is-on",
                          PICKS[item] !== undefined && flash(t, PICKS[item], 300) && "is-press"
                        )}
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="faw-view" key="review">
          <div className="faw-head">
            <h4 className="faw-title faw-title--brand">Review generated assets</h4>
            <span className="faw-ok">
              <CheckCircle2 /> {GENERATED.length} test scenarios · {GEN_CASES} test cases
            </span>
          </div>
          <div className="faw-list">
            {GENERATED.map(scen => {
              const scenAt = REVIEW_AT + 200 + row++ * 230;
              return (
                <div key={scen.id} className={cx("faw-scen faw-pop", t >= scenAt && "is-in")}>
                  <div className="faw-scen__head">
                    <span className={cx("faw-check", t >= scenAt + 1500 && "is-on")}>
                      <Check />
                    </span>
                    <span className="faw-mono">{scen.id}</span>
                    <b>{scen.title}</b>
                    <span className={`faw-tag is-${scen.tag.toLowerCase()}`}>{scen.tag}</span>
                  </div>
                  {scen.cases.map(c => {
                    const caseAt = REVIEW_AT + 200 + row++ * 230;
                    return (
                      <div key={c} className={cx("faw-case faw-pop", t >= caseAt && "is-in")}>
                        <span className={cx("faw-check", t >= caseAt + 1500 && "is-on")}>
                          <Check />
                        </span>
                        <ListChecks />
                        {c}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
          <div className="faw-foot">
            <span className="faw-btn faw-btn--ghost">Regenerate</span>
            <span className={cx("faw-btn", flash(t, INSERT_AT, 300) && "is-press")} data-cur="insert">
              Insert selected ({GEN_CASES})
            </span>
          </div>
        </div>
      )}
    </Screen>
  );
}

/* ── 2. Auto-fill a Test Case ─────────────────────────────────────── */
const TC_NAME = "Signed-in user checks out with a saved address";
const ROBOT_AT = 3000;
const FILL = {
  summary: [4500, "Verify a signed-in customer can place an order with a saved delivery address."],
  priority: [4800, "High"],
  type: [5000, "Functional"],
  pre: [6500, "Customer is signed in and has at least one saved address"],
};
const TC_STEPS = [
  [5300, "Sign in as a registered customer", "The account page opens"],
  [5650, "Add an item to the cart and open checkout", "Checkout shows the cart total"],
  [6000, "Choose a saved address and place the order", "Order is placed and confirmed"],
];
const SUBMIT_AT = 7700;

function Field({ label, req, value, at, t, cur, children, area }) {
  const filled = at === undefined || t >= at;
  return (
    <div>
      <span className="faw-label">
        {label}
        {req && <em>*</em>}
      </span>
      <div
        className={cx(
          "faw-input",
          area && "faw-input--area",
          at !== undefined && t >= ROBOT_AT + 100 && t < at && "is-think",
          at !== undefined && flash(t, at) && "is-ai"
        )}
        data-cur={cur}
      >
        {children || (filled ? value : <span className="faw-ph">{label}</span>)}
      </div>
    </div>
  );
}

function AutofillScene({ t }) {
  const stageRef = useRef(null);
  const thinking = t >= ROBOT_AT + 100 && t < FILL.summary[0];
  const typing = t >= 350 && t < 2600;

  return (
    <Screen
      stageRef={stageRef}
      nav={3}
      crumbs={["Test Case", "Create Test Case"]}
      overlay={
        <>
          <Toast show={t >= SUBMIT_AT + 150}>Test case FSHOP-TC-19 created · AI generated</Toast>
          <Cursor
            stageRef={stageRef}
            t={t}
            path={[
              [0, [62, 97]],
              [350, "name"],
              [ROBOT_AT, "robot"],
              [4200, [74, 52]],
              [SUBMIT_AT, "submit"],
              [8700, [86, 92]],
            ]}
            clicks={[350, ROBOT_AT, SUBMIT_AT]}
          />
        </>
      }
    >
      <div className="faw-view">
        <div className="faw-head">
          <div>
            <h4 className="faw-title">Create New Test Case</h4>
            <span className="faw-sub">Fill the form to create a test case</span>
          </div>
          <span className="faw-btn faw-btn--back">
            <ArrowLeft /> Go Back
          </span>
        </div>
        <div className="faw-form">
          <div>
            <span className="faw-label">
              Test Case Name<em>*</em>
            </span>
            <div className="faw-inrow">
              <div className={cx("faw-input", typing && "is-focus")} data-cur="name">
                {typed(TC_NAME, t, 450, 34) || <span className="faw-ph">Test Case Name</span>}
                {typing && <span className="faw-caret" />}
              </div>
              <span
                className={cx("faw-robot", t >= 2500 && t < ROBOT_AT && "is-hint", flash(t, ROBOT_AT, 300) && "is-press")}
                data-cur="robot"
              >
                {thinking ? <Loader2 className="faw-spin" /> : <FrothAiIcon size={12} />}
              </span>
            </div>
          </div>
          <Field label="Test Scenario Id" req value="FSHOP-TS-4 - Guest and registered checkout" t={t} />
          <Field label="Priority" value={FILL.priority[1]} at={FILL.priority[0]} t={t} />
          <Field label="Testing Type" value={FILL.type[1]} at={FILL.type[0]} t={t} />
          <div className="faw-span2">
            <Field label="Summary" at={FILL.summary[0]} t={t}>
              {t >= FILL.summary[0] ? typed(FILL.summary[1], t, FILL.summary[0], 9) : <span className="faw-ph">Summary</span>}
            </Field>
          </div>
          <Field label="Detail Steps" at={TC_STEPS[0][0]} t={t} area>
            {t < TC_STEPS[0][0] ? (
              <span className="faw-ph">Detail Steps</span>
            ) : (
              <ol className="faw-ol">
                {TC_STEPS.map(([at, step]) => (
                  <li key={step} className={cx("faw-pop", t >= at && "is-in")}>
                    {step}
                  </li>
                ))}
              </ol>
            )}
          </Field>
          <Field label="Expected Result" at={TC_STEPS[0][0] + 150} t={t} area>
            {t < TC_STEPS[0][0] + 150 ? (
              <span className="faw-ph">Expected Result</span>
            ) : (
              <ol className="faw-ol">
                {TC_STEPS.map(([at, , expected]) => (
                  <li key={expected} className={cx("faw-pop", t >= at + 150 && "is-in")}>
                    {expected}
                  </li>
                ))}
              </ol>
            )}
          </Field>
          <div className="faw-span2">
            <Field label="Pre Condition" at={FILL.pre[0]} t={t}>
              {t >= FILL.pre[0] ? typed(FILL.pre[1], t, FILL.pre[0], 11) : <span className="faw-ph">Pre Condition</span>}
            </Field>
          </div>
        </div>
        <div className="faw-foot">
          <span className={cx("faw-aipill", thinking && "is-in")}>
            <FrothAiIcon size={11} /> FrothAI is analysing the context…
          </span>
          <span className="faw-btn faw-btn--ghost">Cancel</span>
          <span className={cx("faw-btn", flash(t, SUBMIT_AT, 300) && "is-press")} data-cur="submit">
            Submit
          </span>
        </div>
      </div>
    </Screen>
  );
}

/* ── 3. Automation Scripts ────────────────────────────────────────── */
const APP_TYPES = [
  { label: "Web", Icon: Globe, color: "#3b82f6" },
  { label: "Android", Icon: Smartphone, color: "#22a06b" },
  { label: "iOS", Icon: Apple, color: "#6b7280" },
  { label: "JS Script", Icon: Code2, color: "#f59e0b" },
];
const APPROVED = [
  "FSHOP-TC-14 - Adding an item updates the cart badge",
  "FSHOP-TC-15 - Changing quantity recalculates the total",
  "FSHOP-TC-16 - Signed-in user can pick a saved address",
];
const S = {
  web: 700,
  ai: 1500,
  tcShow: 1700,
  open: 2400,
  pick: 3200,
  gen: 4100,
  code: 4700,
  live: 9000,
  ask: 11000,
  send: 12250,
  edit: 12550,
};

// Playwright script, as [token class, text] segments per line. `step` ties a line to a test step.
const k = s => ["k", s];
const st = s => ["s", s];
const f = s => ["f", s];
const p = s => ["p", s];
const CODE = [
  { seg: [k("import"), p(" { test, expect } "), k("from"), p(" "), st("'@playwright/test'"), p(";")] },
  { seg: [] },
  { seg: [f("test"), p("("), st("'FSHOP-TC-14 cart badge'"), p(", "), k("async"), p(" ({ page }) => {")] },
  { step: 0, seg: [p("  "), k("await"), p(" page."), f("goto"), p("("), st("'/products/espresso-cup'"), p(");")] },
  {
    step: 1,
    seg: [p("  "), k("await"), p(" page."), f("getByRole"), p("("), st("'button'"), p(", { name: "), st("'Add to cart'"), p(" })."), f("click"), p("();")],
  },
  {
    step: 2,
    seg: [p("  "), k("await"), p(" "), f("expect"), p("(page."), f("getByTestId"), p("("), st("'cart-badge'"), p("))."), f("toHaveText"), p("("), st("'1'"), p(");")],
  },
  {
    added: true,
    seg: [p("  "), k("await"), p(" "), f("expect"), p("(page."), f("getByTestId"), p("("), st("'cart-subtotal'"), p("))."), f("toContainText"), p("("), st("'$12.00'"), p(");")],
  },
  { seg: [p("});")] },
];
const CODE_MS_PER_CHAR = 11;
const SCRIPT_STEPS = ["Open the espresso cup product page", "Click Add to cart", "Cart badge shows 1"];
const ASK_EDIT = "Also check the subtotal shows $12.00";

function CodeLine({ seg, chars }) {
  let left = chars;
  return seg.map(([cls, text], i) => {
    if (left <= 0) return null;
    const part = text.slice(0, left);
    left -= text.length;
    return (
      <span key={i} className={`faw-tok-${cls}`}>
        {part}
      </span>
    );
  });
}

function ScriptsScene({ t }) {
  const stageRef = useRef(null);
  const results = t >= S.gen + 300;
  const menuOpen = t >= S.open && t < S.pick + 150;
  const picked = t >= S.pick;
  const live = t >= S.live;

  // Stream the code, skipping the line FrothAI adds later from the chat.
  let budget = Math.max(0, Math.floor((t - S.code) / CODE_MS_PER_CHAR));
  const lines = CODE.map(line => {
    const len = line.seg.reduce((n, [, s]) => n + s.length, 0);
    if (line.added) return { ...line, chars: t >= S.edit ? len : 0, show: t >= S.edit };
    const chars = Math.min(len, budget);
    budget -= len;
    return { ...line, chars, show: chars > 0 || (len === 0 && budget >= 0) };
  });
  const codeDone = budget >= 0;
  const caretLine = codeDone ? -1 : lines.findIndex(l => !l.added && l.chars < l.seg.reduce((n, [, s]) => n + s.length, 0));
  const stepWritten = i => lines.some(l => l.step === i && l.chars > 0);

  return (
    <Screen
      stageRef={stageRef}
      nav={7}
      crumbs={["Test Lab / Test Automation", "FrothAI Script Generation"]}
      overlay={
        <Cursor
          stageRef={stageRef}
          t={t}
          path={[
            [0, [40, 96]],
            [S.web, "t-Web"],
            [S.ai, "m-ai"],
            [S.open, "tc"],
            [S.pick, "opt-0"],
            [S.gen, "gen"],
            [6200, [72, 88]],
            [S.live, "live"],
            [10700, "ask"],
            [S.send, "send"],
            [13300, [60, 92]],
          ]}
          clicks={[S.web, S.ai, S.open, S.pick, S.gen, S.live, 10700, S.send]}
        />
      }
    >
      {!results ? (
        <div className="faw-view" key="setup">
          <div className="faw-head">
            <div>
              <h4 className="faw-title">Create Automation Script</h4>
              <span className="faw-sub">Fill the form to create an automation script</span>
            </div>
            <span className="faw-btn faw-btn--back">
              <ArrowLeft /> Go Back
            </span>
          </div>
          <span className="faw-label">
            Application Type<em>*</em>
          </span>
          <div className="faw-apps">
            {APP_TYPES.map(({ label, Icon, color }) => (
              <span
                key={label}
                data-cur={`t-${label}`}
                className={cx("faw-opt", label === "Web" && t >= S.web && "is-on", label === "Web" && flash(t, S.web, 300) && "is-press")}
              >
                <Icon style={{ color }} />
                {label}
                <span className="faw-radio" />
              </span>
            ))}
          </div>
          <span className="faw-label">
            Script Creation Method<em>*</em>
          </span>
          <div className="faw-methods">
            <span className="faw-opt faw-opt--tall">
              <span className="faw-opt__icon is-red">
                <Video />
              </span>
              <span>
                <b>Record Manually</b>
                <small>Record your actions in the browser and turn them into an automation script.</small>
              </span>
              <span className="faw-radio" />
            </span>
            <span
              className={cx("faw-opt faw-opt--tall", t >= S.ai && "is-on", flash(t, S.ai, 300) && "is-press")}
              data-cur="m-ai"
            >
              <span className="faw-opt__icon is-ai">
                <Sparkles />
              </span>
              <span>
                <b>Generate with FrothAI</b>
                <small>Let FrothAI generate the script from your test case steps.</small>
              </span>
              <span className="faw-radio" />
            </span>
          </div>
          <div className={cx("faw-tcrow faw-pop", t >= S.tcShow && "is-in")}>
            <div className="faw-tcsel">
              <span className="faw-label">
                Test Case<em>*</em> <small>Approved test cases</small>
              </span>
              <div className={cx("faw-input faw-select", menuOpen && "is-focus")} data-cur="tc">
                {picked ? APPROVED[0] : <span className="faw-ph">Select Test Case</span>}
                <ChevronDown />
              </div>
              <div className={cx("faw-menu", menuOpen && "is-in")}>
                {APPROVED.map((o, i) => (
                  <span key={o} data-cur={`opt-${i}`} className={cx(i === 0 && t >= S.pick - 300 && "is-hover")}>
                    {o}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="faw-label">Script Name</span>
              <div className={cx("faw-input", flash(t, S.pick + 100) && "is-ai")}>
                {picked ? "adding_an_item_updates_the_cart_badge" : <span className="faw-ph">Script Name</span>}
              </div>
            </div>
            <span className={cx("faw-btn faw-btn--ai", flash(t, S.gen, 300) && "is-press")} data-cur="gen">
              <FrothAiIcon size={12} /> Generate Script
            </span>
          </div>
        </div>
      ) : (
        <div className="faw-view" key="results">
          <div className="faw-head">
            <h4 className="faw-title">FrothAI Script Generation</h4>
            <span className={cx("faw-status", codeDone && "is-done")}>
              {codeDone ? <CheckCircle2 /> : <Loader2 className="faw-spin" />}
              {codeDone ? "Script ready" : "Mapping steps to selectors…"}
            </span>
          </div>
          <div className="faw-cols faw-cols--code">
            <div className="faw-card faw-steps">
              <div className="faw-card__title">
                {live ? "Test Live" : "Test Steps"}
                {live && <span className="faw-live-dot">Live</span>}
              </div>
              <ol>
                {SCRIPT_STEPS.map((s, i) => {
                  const runAt = S.live + 400 + i * 550;
                  const state = !live ? (stepWritten(i) ? "mapped" : "") : t >= runAt + 450 ? "pass" : t >= runAt ? "run" : "wait";
                  return (
                    <li key={s} className={state ? `is-${state}` : undefined}>
                      <span className="faw-steps__dot">
                        {state === "pass" || state === "mapped" ? <Check /> : state === "run" ? <Loader2 className="faw-spin" /> : i + 1}
                      </span>
                      <span>{s}</span>
                      {state === "pass" && <em>{["1.4s", "0.6s", "0.3s"][i]}</em>}
                    </li>
                  );
                })}
              </ol>
              {live && t >= S.live + 400 + 3 * 550 && (
                <div className="faw-passed faw-pop is-in">
                  <CheckCircle2 /> 3 of 3 steps passed
                </div>
              )}
              <span className={cx("faw-btn faw-btn--live", flash(t, S.live, 300) && "is-press")} data-cur="live">
                <Play /> Test Live
              </span>
            </div>
            <div className="faw-code">
              <div className="faw-code__bar">
                <span>
                  <Globe /> Playwright · Web
                </span>
                <span className="faw-code__file">adding_an_item_updates_the_cart_badge.spec.js</span>
              </div>
              <pre>
                {lines.map((line, i) =>
                  line.show ? (
                    <div
                      key={i}
                      className={cx(
                        "faw-code__line",
                        line.added && "is-added",
                        live && line.step !== undefined && t >= S.live + 400 + line.step * 550 && t < S.live + 850 + line.step * 550 && "is-run"
                      )}
                    >
                      <span className="faw-code__no">{i + 1}</span>
                      <CodeLine seg={line.seg} chars={line.chars} />
                      {i === caretLine && <span className="faw-caret" />}
                    </div>
                  ) : null
                )}
              </pre>
              <div className="faw-chat">
                {t >= S.send && (
                  <div className="faw-chat__msgs">
                    <span className="faw-bubble faw-bubble--me faw-pop is-in">{ASK_EDIT}</span>
                    {t >= S.edit && (
                      <span className="faw-bubble faw-pop is-in">
                        <FrothAiIcon size={10} /> Added a subtotal check after step 3.
                      </span>
                    )}
                  </div>
                )}
                <div className={cx("faw-chat__box", t >= 10700 && t < S.send && "is-focus")} data-cur="ask">
                  {t >= 10700 && t < S.send ? (
                    <>
                      {typed(ASK_EDIT, t, S.ask, 30)}
                      <span className="faw-caret" />
                    </>
                  ) : (
                    <span className="faw-ph">Ask FrothAI to change the script…</span>
                  )}
                  <span className={cx("faw-chat__send", flash(t, S.send, 300) && "is-press")} data-cur="send">
                    <Send />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Screen>
  );
}

/* ── 4. Ask FrothAI ───────────────────────────────────────────────── */
const QUESTION = "Which requirements have no test cases?";
const A = { bubble: 600, pill: 1700, input: 3700, type: 4100, send: 6200, answer: 8000 };
const ANSWER = [
  "Found 3 requirements with no linked test cases:",
  "- FSHOP-REQ-15: Apply a discount coupon at checkout (To Do | High)",
  "- FSHOP-REQ-18: Save more than one delivery address (In Progress | Medium)",
  "- FSHOP-REQ-21: Export order history as CSV (To Do | Low)",
];
const ANSWER_IDS = ["FSHOP-REQ-15", "FSHOP-REQ-18", "FSHOP-REQ-21"];
const FOLLOW_UPS = ["What are the coverage gaps?", "Which test cases failed in the last run?"];
const CHIPS_AT = A.answer + ANSWER.length * 380 + 200;

function AskScene({ t }) {
  const stageRef = useRef(null);
  const open = t >= A.bubble + 150;
  const typing = t >= A.input && t < A.send;
  const asked = t >= A.send;
  const thinking = asked && t < A.answer;

  return (
    <Screen
      stageRef={stageRef}
      nav={1}
      dim={open}
      crumbs={["Dashboard", "Project Dashboard"]}
      overlay={
        <>
          <span className={cx("faw-fab", open && "is-gone", flash(t, A.bubble, 300) && "is-press")} data-cur="fab">
            <FrothAiIcon size={16} />
          </span>
          <aside className={cx("faw-drawer", open && "is-in")}>
            <div className="faw-drawer__head">
              <FrothAiIcon size={14} />
              <b>Ask FrothAI</b>
              <span className="faw-beta">
                <Zap /> BETA
              </span>
              <X className="faw-drawer__x" />
            </div>
            <div className="faw-drawer__ctx">
              <span className={cx("faw-projpill", t >= A.pill && t < A.pill + 1600 && "is-pulse")}>FrothShop Web Store</span>
              <span>Analytics, coverage, traceability, and semantic search for this project.</span>
            </div>
            <div className="faw-drawer__msgs">
              {asked && <span className="faw-bubble faw-bubble--me faw-pop is-in">{QUESTION}</span>}
              {thinking && (
                <span className="faw-bubble faw-dots">
                  <i />
                  <i />
                  <i />
                </span>
              )}
              {t >= A.answer && (
                <div className="faw-answer faw-pop is-in">
                  {ANSWER.map((line, i) => (
                    <span key={line} className={cx("faw-pop", t >= A.answer + i * 380 && "is-in")}>
                      {line}
                    </span>
                  ))}
                </div>
              )}
              <div className={cx("faw-idchips faw-pop", t >= CHIPS_AT && "is-in")}>
                {ANSWER_IDS.map((id, i) => (
                  <span key={id} data-cur={`id-${i}`} className={cx(i === 0 && t >= 10900 && "is-hover")}>
                    {id}
                  </span>
                ))}
              </div>
              <div className={cx("faw-follow faw-pop", t >= CHIPS_AT + 450 && "is-in")}>
                <small>You may also want to know:</small>
                {FOLLOW_UPS.map(q => (
                  <span key={q}>{q}</span>
                ))}
              </div>
            </div>
            <div className="faw-drawer__input">
              <div className={cx("faw-chat__box", typing && "is-focus")} data-cur="input">
                {typing ? (
                  <>
                    {typed(QUESTION, t, A.type, 40)}
                    <span className="faw-caret" />
                  </>
                ) : (
                  <span className="faw-ph">Ask about this project…</span>
                )}
              </div>
              <span className={cx("faw-btn faw-btn--send", flash(t, A.send, 300) && "is-press")} data-cur="send">
                Send
              </span>
            </div>
          </aside>
          <Cursor
            stageRef={stageRef}
            t={t}
            path={[
              [0, [70, 70]],
              [A.bubble, "fab"],
              [A.pill, [64, 26]],
              [A.input, "input"],
              [A.send, "send"],
              [7400, [80, 72]],
              [10900, "id-0"],
              [11800, "id-0"],
            ]}
            clicks={[A.bubble, A.input, A.send]}
          />
        </>
      }
    >
      <div className="faw-view">
        <div className="faw-tabs">
          {["Overview", "Requirements", "Test Management", "Automation", "Defects"].map((tab, i) => (
            <span key={tab} className={i === 2 ? "is-on" : undefined}>
              {tab}
            </span>
          ))}
        </div>
        <h4 className="faw-title">Test Management Analytics</h4>
        <div className="faw-dash">
          {[
            ["Test Case Status", "conic-gradient(#1f7a55 0 78%, #f59e0b 0 90%, #e5484d 0)"],
            ["Test Type Distribution", "conic-gradient(#4f46e5 0 46%, #be185d 0 70%, #0ea5e9 0)"],
            ["Test Plan Status", "conic-gradient(#6d28d9 0 100%)"],
            ["Automation Coverage", "conic-gradient(#d97706 0 62%, #e6e8ee 0)"],
          ].map(([title, bg]) => (
            <div key={title} className="faw-dash__card">
              <span>{title}</span>
              <i style={{ background: bg }} />
            </div>
          ))}
        </div>
      </div>
    </Screen>
  );
}

export const SCENES = {
  requirements: { ms: 13200, stepAt: [0, 4300, REVIEW_AT], Scene: RequirementsScene },
  autofill: { ms: 9200, stepAt: [0, ROBOT_AT], Scene: AutofillScene },
  scripts: { ms: 14000, stepAt: [0, 1900, S.live], Scene: ScriptsScene },
  ask: { ms: 12600, stepAt: [0, A.input, A.answer], Scene: AskScene },
};
